/**
 * Junior Company Teilnahme einer Unterstützer:in (Anforderung #181).
 *
 * Gefragt wird an vier Stellen — Registrierung (Abschnitt 4), Aktivierung
 * einer Einladung, eigenes Profil und Bearbeiten-Fenster der Verwaltung. Die
 * Regeln stehen deshalb hier und nicht viermal in den Formularen; das Backend
 * prüft dieselben (`services/supporter/juniorAlumni.js`).
 *
 * `juniorAlumni` kennt drei Zustände: true (ja), false (nein) und null (nicht
 * angegeben — so stehen alle Profile von vor der Anforderung da). Der Schieber
 * zeigt bei null „Nein"; gespeichert wird null erst dann als Antwort, wenn
 * jemand den Schieber tatsächlich bedient hat.
 */

export const JUNIOR_ALUMNI_MIN_YEAR = 1960

export const currentYear = () => new Date().getFullYear()

/** Leerer Formularstand für Registrierung und Aktivierung: Vorgabe „Nein". */
export const emptyJuniorAlumni = () => ({
	juniorAlumni: false,
	juniorAlumniSchool: '',
	juniorAlumniYear: '',
	juniorAlumniStates: []
})

/**
 * Fehler je Feld — Schlüssel wie die Felder, Werte als i18n-Schlüssel.
 * Leeres Objekt heißt: in Ordnung.
 */
export const juniorAlumniErrors = value => {
	if (!value || value.juniorAlumni !== true) return {}

	const errors = {}

	if (!String(value.juniorAlumniSchool || '').trim()) errors.juniorAlumniSchool = 'Required field'

	const year = String(value.juniorAlumniYear ?? '').trim()
	if (!year) {
		errors.juniorAlumniYear = 'Required field'
	} else {
		const number = Number(year)
		if (!Number.isInteger(number) || number < JUNIOR_ALUMNI_MIN_YEAR || number > currentYear()) {
			errors.juniorAlumniYear = 'juniorAlumniYearRange'
		}
	}

	if (!(value.juniorAlumniStates || []).length) errors.juniorAlumniStates = 'juniorAlumniStatesRequired'

	return errors
}

/**
 * Die Felder für den Server. Bei „nein" gehen Schule, Jahr und Bundesländer
 * leer hinaus, damit nach einem Wechsel von ja auf nein nichts stehen bleibt.
 *
 * `keepUnanswered`: Im Profil bleibt ein nie beantwortetes null so, wie es
 * ist — wer nur die Telefonnummer ändert, hat die Frage nicht mit „nein"
 * beantwortet. Dann gehen die Felder gar nicht mit.
 */
export const juniorAlumniPayload = (value, { keepUnanswered = false } = {}) => {
	if (!value || value.juniorAlumni === null || value.juniorAlumni === undefined) {
		return keepUnanswered ? {} : { juniorAlumni: false, ...clearedFields }
	}

	if (value.juniorAlumni !== true) return { juniorAlumni: false, ...clearedFields }

	const year = String(value.juniorAlumniYear ?? '').trim()

	return {
		juniorAlumni: true,
		juniorAlumniSchool: String(value.juniorAlumniSchool || '').trim(),
		juniorAlumniYear: year ? Number(year) : null,
		juniorAlumniStates: value.juniorAlumniStates || []
	}
}

const clearedFields = {
	juniorAlumniSchool: null,
	juniorAlumniYear: null,
	juniorAlumniStates: []
}
